import ajax from './ajax';
import ERRORS from './errors';
import Console from './console';
import TokenStorage from './token_storage';

const console = new Console(window.Settings.SESSIONWIDGET_DEBUG);

class SswCore {
    constructor(settings = {}) {
        this.storage = new TokenStorage(settings);
        this.tokenURL = settings.SESSIONWIDGET_TOKEN_URL;
        this.apiUrl = settings.SESSIONWIDGET_REVOKE_TOKEN_API;
        this.accountInfoUrl = settings.ACCOUNT_INFO_API_URL;
    }

    static createCallbackIframe(url, clientId) {
        const iframe = document.createElement('iframe');

        iframe.id = clientId;
        iframe.name = `sessionwidget_callback_${clientId}`;
        iframe.src = url;
        iframe.width = 0;
        iframe.height = 0;
        iframe.style.display = 'none';
        iframe.style.visibility = 'hidden';
        document.body.appendChild(iframe);
        return iframe;
    }

    static jsonParse(data) {
        let parsed = {};

        if (data && typeof data === 'string') {
            try {
                parsed = JSON.parse(data);
            } catch (e) {
                parsed = {};
            }
        }
        return parsed;
    }

    requestToken(clientId, scope, requestEvent) {
        const urlStart = `${this.tokenURL}?response_type=token&client_id=${clientId}`;
        const originQuery = `&origin=${encodeURIComponent(requestEvent.origin)}`;
        const scopeQuery = scope ? `&scope=${scope}` : '';
        const url = `${urlStart}${scopeQuery}${originQuery}`;

        return new Promise((resolve, reject) => {
            const callbackIframe = SswCore.createCallbackIframe(url, clientId);
            const tokenRecievedHandler = (event) => {
                if (event.source !== callbackIframe.contentWindow) {
                    return;
                }

                const responseData = event.data;

                responseData.clientId = clientId;
                if (responseData.error) {
                    console.log('Widget: Recieved token error: ', responseData);
                    if (this.storage.authenticatedState === 0 && responseData.error === 'access_denied') {
                        responseData.scope = scope;
                        this.storage.set(clientId, scope, responseData);
                    }
                    reject(responseData);
                } else {
                    console.log('Widget: Recieved token: ', responseData);
                    responseData.session = SswCore.jsonParse(responseData.session);
                    responseData.account = SswCore.jsonParse(responseData.account);
                    this.storage.set(clientId, scope, responseData);
                    resolve(responseData);
                }

                window.removeEventListener('message', tokenRecievedHandler);
                document.body.removeChild(callbackIframe);
            };

            window.addEventListener('message', tokenRecievedHandler, false);
        });
    }

    handleTokenRequest(event, clientId, scope) {
        const tokenData = this.storage.get(clientId, scope);

        if (tokenData) {
            return Promise.resolve(tokenData);
        }
        return this.requestToken(clientId, scope, event);
    }

    revokeToken(event, clientId, scope) {
        const token = this.storage.remove(clientId, scope);

        if (token) {
            const query = `token=${token.access_token}&client_id=${clientId}`;

            ajax(this.apiUrl, 'POST', query);
        }
        return Promise.resolve(null);
    }

    requestAccountInfo(event, token) {
        const count = 15;

        return new Promise((resolve, reject) => {
            const xhr = ajax(
                this.accountInfoUrl,
                'POST', 'fields=id,nickname',
                [['Authorization', `${token.token_type} ${token.access_token}`]]
            );

            xhr.onload = () => this.onResponseAccountInfo(count, xhr).then((info) => {
                const updatedToken = Object.assign({}, token, { account: info });

                this.storage.set(event.data.clientId, token.scope, updatedToken);
                resolve({ request: info });
            }).catch(reject);
            xhr.onerror = reject;
        });
    }

    retriveAccountInfo(count, statusUrl) {
        return new Promise((resolve, reject) => setTimeout(() => {
            const xhr = ajax(statusUrl);

            xhr.onload = () => this.onResponseAccountInfo(count, xhr)
                                   .then(resolve).catch(reject);
            xhr.onerror = reject;
        }, 1000));
    }

    onResponseAccountInfo(count, xhr) {
        let accountInfo;

        switch (xhr.status) {
            case 200:
                accountInfo = SswCore.jsonParse(xhr.responseText);
                return Promise.resolve(accountInfo);
            case 202:
                if (count < 1) {
                    return Promise.reject({ error: ERRORS.TIMEOUT });
                }
                return this.retriveAccountInfo(count - 1, xhr.getResponseHeader('Location'));
            case 403:
                return Promise.reject({ error: ERRORS.FORBIDDEN });
            case 401:
                return Promise.reject({ error: ERRORS.UNAUTHORIZED });
            default:
                return Promise.reject({ error: ERRORS.DEFAULT });
        }
    }

    getMenuToken(event, clientId) {
        const token = this.storage.filter(clientId, 'session.menu_token')[0];

        if (token && (token.error || token.session)) {
            if (token.error) {
                return Promise.resolve(token);
            } else if (token.session) {
                return Promise.resolve({ request: token.session.menu_token });
            }
        }
        return this.requestToken(clientId, 'session.menu_token', event)
            .then((tokenData = {}) => ({ request: tokenData.session.menu_token }));
    }

    getAccountId(event, clientId) {
        const token = this.storage.filter(clientId)[0];

        if (token) {
            if (token.error) {
                return Promise.resolve(token);
            } else if (token.account) {
                return Promise.resolve({ request: token.account.id });
            }
        }
        return this.requestToken(clientId, null, event)
            .then((tokenData = {}) => ({ request: tokenData.account.id }));
    }

    getAccountInfo(event, clientId) {
        const token = this.storage.filter(clientId, 'openid')[0];

        if (token) {
            if (token.error) {
                return Promise.resolve(token);
            } else if (token.account && token.account.nickname) {
                return Promise.resolve({ request: token.account });
            }
            return this.requestAccountInfo(event, token);
        }
        return this.requestToken(clientId, 'openid', event)
            .then((tokenData) => this.requestAccountInfo(event, tokenData));
    }
}

export default SswCore;



// WEBPACK FOOTER //
// ./static_source/js/ssw_core.js